import React, { useState, useEffect, useMemo } from 'react';
import { supabase } from '../supabaseClient';
import { useDataContext } from '../DataContext';
import './Discover.css';

const DEFAULT_AVATAR = '/img/default.png';

export default function Discover({ session }) {
  const [myTags, setMyTags] = useState([]);
  const [users, setUsers] = useState([]);
  const [pokes, setPokes] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const { loadDiscoverData, updatePokes } = useDataContext();

  useEffect(() => {
    const initializeData = async () => {
      const data = await loadDiscoverData(session.user.email);
      if (data) {
        setMyTags(data.myTags);
        setUsers(data.users);
        setPokes(data.pokes);
      }
      setLoading(false);
    };

    initializeData();
  }, [session.user.email, loadDiscoverData]);

  const sendPoke = async (toUser) => {
    try {
      await supabase
        .from('pokes')
        .insert({
          from_user: session.user.email,
          to_user: toUser,
        });

      const newPokes = [...pokes, toUser];
      setPokes(newPokes);
      updatePokes(newPokes);
    } catch (error) {
      console.error('Error sending poke:', error);
    }
  };

  // Calculate tag weights
  const tagWeights = useMemo(() => {
    const counts = {};
    [...users, { tags: myTags }].forEach(u => {
      u.tags?.forEach(t => {
        counts[t.tag] = (counts[t.tag] || 0) + 1;
      });
    });

    const weights = {};
    Object.keys(counts).forEach(tag => {
      weights[tag] = 1 / Math.sqrt(counts[tag]);
    });
    return weights;
  }, [users, myTags]);

  // Calculate match percentage
  const calculateMatch = useMemo(() => {
    return (userTags) => {
      if (!myTags || myTags.length === 0 || !userTags || userTags.length === 0) {
        return 0;
      }

      const myTagSet = new Set(myTags.map(t => t.tag));
      const theirTagSet = new Set(userTags.map(t => t.tag));
      const intersection = [...myTagSet].filter(t => theirTagSet.has(t));

      if (intersection.length === 0) return 0;

      const weightedScore = intersection.reduce((sum, tag) => {
        return sum + (tagWeights[tag] || 0);
      }, 0);

      const maxPossible = Math.max(
        [...myTagSet].reduce((sum, tag) => sum + (tagWeights[tag] || 0), 0),
        [...theirTagSet].reduce((sum, tag) => sum + (tagWeights[tag] || 0), 0)
      );

      return Math.round((weightedScore / maxPossible) * 100);
    };
  }, [myTags, tagWeights]);

  // Sort users by match percentage
  const sortedUsers = useMemo(() => {
    return users
      .map(u => ({ ...u, match: calculateMatch(u.tags) }))
      .sort((a, b) => b.match - a.match)
      .slice(0, 100);
  }, [users, calculateMatch]);

  if (loading) {
    return <div className="loading">loading...</div>;
  }

  if (myTags.length === 0) {
    return (
      <div className="empty-state">
        <p>add some tags to your profile first</p>
      </div>
    );
  }

  if (sortedUsers.length === 0) {
    return (
      <div className="empty-state">
        <p>no other users yet</p>
      </div>
    );
  }

  return (
    <div className="discover">
      {sortedUsers.map(user => {
        const hasPoked = pokes.includes(user.email);
        const sharedTags = user.tags.filter(t => 
          myTags.some(mt => mt.tag === t.tag)
        );
        const otherTags = user.tags.filter(t => 
          !myTags.some(mt => mt.tag === t.tag)
        );

        return (
          <div key={user.email} className="user-card">
            <div 
              className="avatar" 
              style={{ 
                backgroundImage: user.profile_image 
                  ? `url(${user.profile_image})` 
                  : `url(${DEFAULT_AVATAR})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat'
              }}
            />
            <div className="content">
              <div className="header">
                <div className="username">{user.username || 'anonymous'}</div>
                <div className="match">{user.match}%</div>
              </div>
              
              {user.bio && <div className="bio">{user.bio}</div>}
              
              <div className="tags-section">
                {sharedTags.length > 0 && (
                  <div>
                    <div className="tags-label">matching tags</div>
                    <div className="tags-row">
                      {sharedTags.slice(0, 15).map(t => (
                        <span key={`${t.tag}-${t.category}`} className="tag shared">
                          {t.tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                
                {otherTags.length > 0 && (
                  <div>
                    <div className="tags-label">other tags</div>
                    <div className="tags-row">
                      {otherTags.slice(0, 20).map(t => (
                        <span key={`${t.tag}-${t.category}`} className="tag small">
                          {t.tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {hasPoked && (
                <div className="poked-status">
                  poked ✓
                </div>
              )}
            </div>

            {!hasPoked && (
              <button 
                className="poke-btn"
                onClick={() => sendPoke(user.email)}
              >
                poke
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}